import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

import { NumberUtils } from '@/utils/numbers.utils';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

export type ProcessingStatus = 'pending' | 'processing' | 'success' | 'error';

export interface ContractUpdateRow {
  contractId: string;
  contractNumber?: string;
  contractStartDate: string;
  contractEndDate: string;
  initialTerm: number | undefined;
  renewableTerm: number | undefined;
  errors?: string;
  status?: ProcessingStatus;
}

enum ColumnNames {
  ContractID = 'Contract ID',
  ContractNumber = 'Contract Number',
  ContractStartDate = 'Contract Start Date',
  ContractEndDate = 'Contract End Date',
  InitialTerm = 'Initial Term',
  RenewableTerm = 'Renewable Term',
}

const DATE_FORMAT = 'YYYY-MM-DD';

const parseIntOrUndefined = (value: string): number | undefined => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const parsed = NumberUtils.parseIntegerInput(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizeColumnName = (name: string): string => {
  return name.replace(/\s+/g, '').replace(/\(.*?\)/g, '').toLowerCase();
};

const findColumnValue = (row: any, targetColumn: string): string => {
  const normalizedTarget = normalizeColumnName(targetColumn);

  for (const [key, value] of Object.entries(row)) {
    const normalizedKey = normalizeColumnName(key);
    if (normalizedTarget === normalizedKey) {
      return value as string || '';
    }
  }
  return '';
};

const excelSerialToDate = (serial: number): Date => {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const excelEpoch = Date.UTC(1899, 11, 30);
  return new Date(excelEpoch + serial * millisecondsPerDay);
};

const toDisplayDate = (raw: unknown): string | null => {
  if (raw === null || raw === undefined || raw === '') return '';

  if (typeof raw === 'number') {
    const dateFromSerial = excelSerialToDate(raw);
    if (!isNaN(dateFromSerial.getTime())) {
      return dayjs.utc(dateFromSerial).format(DATE_FORMAT);
    }
  }

  const str = String(raw).trim();
  if (!str) return '';

  const d = dayjs.utc(str, DATE_FORMAT, true);
  if (d.isValid()) return d.format(DATE_FORMAT);

  return null;
};

const parseCSV = (file: File): Promise<ContractUpdateRow[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        try {
          const data = results.data.map((row: any) => {
            const startRaw = findColumnValue(row, ColumnNames.ContractStartDate);
            const endRaw = findColumnValue(row, ColumnNames.ContractEndDate);
            const startDate = toDisplayDate(startRaw);
            const endDate = toDisplayDate(endRaw);

            let errors = '';
            if (startRaw && startDate === null) {
              errors = 'Invalid Contract Start Date format. Expected YYYY-MM-DD.';
            }
            if (endRaw && endDate === null) {
              errors = errors
                ? `${errors} Invalid Contract End Date format. Expected YYYY-MM-DD.`
                : 'Invalid Contract End Date format. Expected YYYY-MM-DD.';
            }

            return {
              contractId: findColumnValue(row, ColumnNames.ContractID),
              contractNumber: findColumnValue(row, ColumnNames.ContractNumber),
              contractStartDate: startDate || '',
              contractEndDate: endDate || '',
              initialTerm: parseIntOrUndefined(findColumnValue(row, ColumnNames.InitialTerm)),
              renewableTerm: parseIntOrUndefined(findColumnValue(row, ColumnNames.RenewableTerm)),
              errors,
              status: errors ? ('error' as const) : ('pending' as const),
            };
          });
          resolve(data.filter(row => row.contractId || row.contractNumber));
        } catch {
          reject(new Error('Failed to parse CSV file'));
        }
      },
      error: () => reject(new Error('Failed to read CSV file')),
    });
  });
};

const parseExcel = async (file: File): Promise<ContractUpdateRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          resolve([]);
          return;
        }

        const [headers, ...rows] = jsonData as any[][];
        const result = rows.map((row: any[]) => {
          const rowObj: any = {};
          headers.forEach((header: string, index: number) => {
            rowObj[header] = row[index] || '';
          });
          const startRaw = findColumnValue(rowObj, ColumnNames.ContractStartDate);
          const endRaw = findColumnValue(rowObj, ColumnNames.ContractEndDate);
          const startDate = toDisplayDate(startRaw);
          const endDate = toDisplayDate(endRaw);

          let errors = '';
          if (startRaw && startDate === null) {
            errors = 'Invalid Contract Start Date format. Expected YYYY-MM-DD.';
          }
          if (endRaw && endDate === null) {
            errors = errors
              ? `${errors} Invalid Contract End Date format. Expected YYYY-MM-DD.`
              : 'Invalid Contract End Date format. Expected YYYY-MM-DD.';
          }

          return {
            contractId: findColumnValue(rowObj, ColumnNames.ContractID),
            contractNumber: findColumnValue(rowObj, ColumnNames.ContractNumber),
            contractStartDate: startDate || '',
            contractEndDate: endDate || '',
            initialTerm: parseIntOrUndefined(findColumnValue(rowObj, ColumnNames.InitialTerm)),
            renewableTerm: parseIntOrUndefined(findColumnValue(rowObj, ColumnNames.RenewableTerm)),
            errors,
            status: errors ? ('error' as const) : ('pending' as const),
          };
        }).filter(row => row.contractId || row.contractNumber);

        resolve(result);
      } catch {
        reject(new Error('Failed to parse Excel file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read Excel file'));
    reader.readAsArrayBuffer(file);
  });
};

export const generateCSV = (data: ContractUpdateRow[]): string => {
  const headers = [
    'Contract ID', 'Contract Number', 'Contract Start Date (YYYY-MM-DD)', 'Contract End Date (YYYY-MM-DD)',
    'Initial Term', 'Renewable Term', 'Errors'
  ];
  const rows = data.map(row => [
    row.contractId,
    `${row.contractNumber  || ''}`,
    row.contractStartDate,
    row.contractEndDate,
    row.initialTerm,
    row.renewableTerm,
    row.errors || '',
  ]);

  return Papa.unparse([headers, ...rows]);
};

export const generateExcel = (data: ContractUpdateRow[]): ArrayBuffer => {
  const headers = [
    'Contract ID', 'Contract Number', 'Contract Start Date (YYYY-MM-DD)', 'Contract End Date (YYYY-MM-DD)',
    'Initial Term', 'Renewable Term', 'Errors'
  ];
  const rows = data.map(row => [
    row.contractId,
    `${row.contractNumber  || ''}`,
    row.contractStartDate,
    row.contractEndDate,
    row.initialTerm,
    row.renewableTerm,
    row.errors || '',
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Bulk Update Results');

  return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
};

export const parseFile = async (file: File): Promise<ContractUpdateRow[]> => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  if (fileExtension === 'csv') {
    return parseCSV(file);
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    return parseExcel(file);
  } else {
    throw new Error('Unsupported file format. Please upload CSV or Excel files.');
  }
};

export const downloadFile = (content: string | ArrayBuffer, filename: string, type: 'csv' | 'excel' = 'csv') => {
  let blob: Blob;

  if (type === 'excel') {
    blob = new Blob([content as ArrayBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  } else {
    blob = new Blob([content as string], { type: 'text/csv;charset=utf-8;' });
  }

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
